import React, { useEffect, useState } from "react";
import axios from "axios";
import Card from "../Components/Card";
import SimpleTable from "../Components/SimpleTable";
import Badge from "../Components/Badge";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import BlueButton from "../Components/BlueButton";
import { BRAND } from "../css/brand";
import { RefreshCw, Download } from "lucide-react";
import TableSkeleton from "./TableSkeleton";

export default function PullbackKeysReport({ brand, CompanyID, receiverId }) {
  const [pullbackReports, setPullbackReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 10;

  const loginUserID = localStorage.getItem("userId"); // ✅ Get from session/local storage if available

  const fetchPullbackReports = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        mode: 2,
        company_id: CompanyID,
        page,
        per_page: rowsPerPage,
      };

      if (receiverId) params.receiver_id = receiverId;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const res = await axios.get(
        `https://spillas.com/api/v1/keys/report?page=${page}&per_page=${rowsPerPage}`,
        { params }
      );

      setPullbackReports(res.data.data || []);
      setTotalPages(res.data.last_page || 1);
    } catch (err) {
      console.error("Failed to fetch pullback reports:", err);
      setError("Failed to load pullback report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPullbackReports(currentPage);
  }, [CompanyID, receiverId, currentPage, fromDate, toDate]);

  // 🔹 CSV Download Handler
  const handleDownload = async () => {
    try {
      if (!CompanyID) {
        alert("Missing Company ID");
        return;
      }

      let url = `https://spillas.com/api/v1/keys/report?mode=2&company_id=${CompanyID}&page=1&per_page=10000`;
      if (receiverId) url += `&receiver_id=${receiverId}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;

      const res = await axios.get(url);
      const rows = res.data?.data || [];

      if (!rows.length) {
        alert("No data to download");
        return;
      }

      const headers = [
        "Sr No.",
        "Date",
        "Previous Holder (Code)",
        "Transferred To (Code)",
        "Processed By",
        "Role",
        "Package",
        "Count",
        "Rate",
        "Amount",
        "Status",
      ];

      const csvRows = [
        headers.join(","),
        ...rows.map((rep, i) =>
          [
            i + 1,
            new Date(rep.created_at).toLocaleDateString(),
            `${rep.sender_name} (${rep.sender_code || "N/A"})`,
            `${rep.receiver_name} (${rep.receiver_code || "N/A"})`,
            rep.created_by_name || "-",
            (() => {
              switch (rep.role) {
                case 3:
                  return "CNF";
                case 4:
                  return "Super Stockist";
                case 5:
                  return "Distributor";
                case 7:
                  return "Retailer";
                default:
                  return "Unknown";
              }
            })(),
            rep.package_name,
            rep.qty,
            rep.key_rate,
            rep.amount,
            "Received",
          ].join(",")
        ),
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const urlObject = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObject;
      link.download = `pullback_keys_report.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(urlObject);
    } catch (error) {
      console.error("Failed to download CSV:", error);
      alert("Failed to download report");
    }
  };

  const getRoleBadge = (roleId) => {
    const baseStyle =
      "px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 w-fit";
    switch (roleId) {
      case 3:
        return (
          <span className={`${baseStyle} bg-blue-50 border-blue-300`}>
            CNF
          </span>
        );
      case 4:
        return (
          <span className={`${baseStyle} bg-purple-50 border-purple-300`}>
            Super Stockist
          </span>
        );
      case 5:
        return (
          <span className={`${baseStyle} bg-orange-50 border-orange-300`}>
            Distributor
          </span>
        );
      case 7:
        return (
          <span className={`${baseStyle} bg-green-50 border-green-300`}>
            Retailer
          </span>
        );
      default:
        return (
          <span className={`${baseStyle} bg-gray-50 border-gray-300`}>
            Unknown
          </span>
        );
    }
  };

  return (
    <Card brand={brand} title="Pullback Keys Report">
      {/* 🔹 Filters */}
      <div className="flex flex-wrap justify-end items-center mb-3 gap-3">
        <div className="flex items-center gap-2 justify-center">
          <label className="text-sm font-medium text-gray-700">
            Date Range:
          </label>
          <DatePicker
            selectsRange
            startDate={fromDate ? new Date(fromDate) : null}
            endDate={toDate ? new Date(toDate) : null}
            onChange={(dates) => {
              const [start, end] = dates;
              setFromDate(start ? start.toISOString().split("T")[0] : "");
              setToDate(end ? end.toISOString().split("T")[0] : "");
            }}
            dateFormat="yyyy-MM-dd"
            placeholderText="Select date range"
            className="border rounded-lg px-3 py-1 text-sm shadow-sm w-64 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <BlueButton
          brand={brand}
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          Reset
        </BlueButton>

        <BlueButton
          brand={BRAND}
          onClick={() => fetchPullbackReports()}
          className="inline-flex items-center gap-1"
        >
          <RefreshCw size={14} /> Refresh
        </BlueButton>

        {/* ✅ Download Button */}
        <BlueButton
          brand={BRAND}
          onClick={handleDownload}
          className="inline-flex items-center gap-1"
        >
          <Download size={14} /> Download
        </BlueButton>
      </div>

      {/* 🔹 Table Section */}
      {loading ? (
        <TableSkeleton
          rows={pullbackReports?.length || rowsPerPage}
          headers={[
            "Sr No.",
            "Date",
            "Previous Holder",
            "Transferred To",
            "Processed By",
            "Package",
            "Count",
            "Rate",
            "Amount",
            "Status",
          ]}
        />
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : pullbackReports.length === 0 ? (
        <p>No data found</p>
      ) : (
        <>
          <SimpleTable
            headers={[
              "Sr No.",
              "Date",
              "Previous Holder",
              "Transferred To",
              "Processed By",
              "Role",
              "Package",
              "Count",
              "Rate",
              "Amount",
              "Status",
            ]}
            rows={pullbackReports.map((rep, i) => [
              (currentPage - 1) * rowsPerPage + i + 1,
              new Date(rep.created_at).toLocaleDateString(),
              `${rep.sender_name} (${rep.sender_code || "N/A"})`,
              `${rep.receiver_name} (${rep.receiver_code || "N/A"})`,
              `${rep.created_by_name || "-"}`,
              getRoleBadge(rep.role),
              rep.package_name,
              rep.qty,
              `₹${rep.key_rate}`,
              `₹${rep.amount}`,
              Badge("Received", "green"),
            ])}
          />

          {/* 🔹 Pagination */}
          <div className="flex gap-2 justify-end mt-4 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className={`px-2 py-1 border rounded text-sm ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-slate-100"
              }`}
            >
              Prev
            </button>

            {/* Page Numbers */}
            {(() => {
              const pages = [];
              const maxVisible = 5;
              let start = Math.max(1, currentPage - 2);
              let end = Math.min(totalPages, start + maxVisible - 1);

              if (end - start < maxVisible - 1) {
                start = Math.max(1, end - maxVisible + 1);
              }

              if (start > 1) {
                pages.push(1);
                if (start > 2) pages.push("...");
              }

              for (let i = start; i <= end; i++) pages.push(i);

              if (end < totalPages) {
                if (end < totalPages - 1) pages.push("...");
                pages.push(totalPages);
              }

              return pages.map((p, idx) =>
                p === "..." ? (
                  <span key={idx} className="px-2 text-gray-500">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-2 py-1 border rounded text-sm ${
                      currentPage === p
                        ? "bg-blue-600 text-white"
                        : "bg-white hover:bg-slate-100"
                    }`}
                  >
                    {p}
                  </button>
                )
              );
            })()}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
              className={`px-2 py-1 border rounded text-sm ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-slate-100"
              }`}
            >
              Next
            </button>
          </div>
        </>
      )}
    </Card>
  );
}



// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Card from "../Components/Card";
// import SimpleTable from "../Components/SimpleTable";
// import Badge from "../Components/Badge";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import BlueButton from "../Components/BlueButton";
// import { BRAND } from "../css/brand";
// import { RefreshCw } from "lucide-react";
// import Loader from "./Loader";
// import TableSkeleton from "./TableSkeleton";

// export default function PullbackKeysReport({ brand, CompanyID, receiverId }) {
//   const [pullbackReports, setPullbackReports] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const rowsPerPage = 10;
  

  
//     const fetchPullbackReports = async (page = 1) => {
//       setLoading(true);
//       setError(null);
//       try {
//         const params = {
//           mode: 2,
//           company_id: CompanyID,
//           page: currentPage,
//           per_page: rowsPerPage,
//         };

//         // ✅ If receiverId is provided, include it along with company_id
//         if (receiverId) {
//           params.receiver_id = receiverId;
//         }

//         if (fromDate) params.from_date = fromDate;
//         if (toDate) params.to_date = toDate;

//         const res = await axios.get(`https://spillas.com/api/v1/keys/report?page=${page}&per_page=${rowsPerPage}`, {
//           params,
//         });

//         setPullbackReports(res.data.data || []);
//         setTotalPages(res.data.last_page || 1);
//       } catch (err) {
//         console.error("Failed to fetch pullback reports:", err);
//         setError("Failed to load pullback report");
//       } finally {
//         setLoading(false);
//       }
//     };
//   useEffect(() => {
//     fetchPullbackReports();
//   }, [CompanyID, receiverId, currentPage, fromDate, toDate]);

//   const getRoleBadge = (roleId) => {
//   const baseStyle =
//     "px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 w-fit";

//   switch (roleId) {
//     case 3:
//       return (
//         <span className={`${baseStyle}  bg-blue-50 border-blue-300`}>
//           <i className="ri-information-line"></i> CNF
//         </span>
//       );
//     case 4:
//       return (
//         <span className={`${baseStyle} bg-purple-50 border-purple-300`}>
//           <i className="ri-information-line"></i> Super Stockist
//         </span>
//       );
//     case 5:
//       return (
//         <span className={`${baseStyle} bg-orange-50 border-orange-300`}>
//           <i className="ri-information-line"></i> Distributor
//         </span>
//       );
//     case 7:
//       return (
//         <span className={`${baseStyle} bg-green-50 border-green-300`}>
//           <i className="ri-information-line"></i> Retailer
//         </span>
//       );
//     default:
//       return (
//         <span className={`${baseStyle} text-gray-600 bg-gray-50 border-gray-300`}>
//           Unknown
//         </span>
//       );
//   }
// };

//   return (
//     <Card brand={brand} title="Pullback Keys Report">
//       {/* 🔹 Filters */}
//       <div className="flex flex-wrap justify-end items-center mb-3 gap-3">
//         <div className="flex items-center gap-2 justify-center">
//           <label className="text-sm font-medium text-gray-700">Date Range:</label>
//           <DatePicker
//             selectsRange
//             startDate={fromDate ? new Date(fromDate) : null}
//             endDate={toDate ? new Date(toDate) : null}
//             onChange={(dates) => {
//               const [start, end] = dates;
//               setFromDate(start ? start.toISOString().split("T")[0] : "");
//               setToDate(end ? end.toISOString().split("T")[0] : "");
//             }}
//             dateFormat="yyyy-MM-dd"
//             placeholderText="Select date range"
//             className="border rounded-lg px-3 py-1 text-sm shadow-sm w-64 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
//           />
//         </div>


//         <BlueButton
//           brand={brand}
//           onClick={() => {
//             setFromDate("");
//             setToDate("");
//           }}
//           className="border px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
//         >
//           Reset
//         </BlueButton>

//         <BlueButton brand={BRAND} onClick={() => {fetchPullbackReports()}} className="inline-flex items-center gap-1">
//           <RefreshCw size={14} /> Refresh
//         </BlueButton>
//       </div>

//       {/* 🔹 Table Section */}
//       {loading ? (
//         <TableSkeleton rows={pullbackReports?.length || rowsPerPage}  headers={[
//               "Sr No.",
//               "Date",
//               "Previous Holder",
//               "Transferred To",
//               "Processed By",
//               "Package",
//               "Count",
//               "Rate",
//               "Amount",
//               "Status",
//             ]}/>
//       ) : error ? (
//         <p className="text-red-500">{error}</p>
//       ) : pullbackReports.length === 0 ? (
//         <p>No data found</p>
//       ) : (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No.",
//               "Date",
//               "Previous Holder",
//               "Transferred To",
//               "Processed By",
//               "Role",
//               "Package",
//               "Count",
//               "Rate",
//               "Amount",
//               "Status",
//             ]}
//             rows={pullbackReports.map((rep, i) => [
//               (currentPage - 1) * rowsPerPage + i + 1, // ✅ fix index
//               new Date(rep.created_at).toLocaleDateString(),
//               `${rep.sender_name} (${rep.sender_code || "N/A"})`,
//               `${rep.receiver_name} (${rep.receiver_code || "N/A"})`,
//               `${rep.created_by_name || "-"}`,
//               getRoleBadge(rep.role),
//               rep.package_name,
//               rep.qty,
//               `₹${rep.key_rate}`,
//               `₹${rep.amount}`,
//               Badge("Received", "green"),
//             ])}

//           />

//           {/* 🔹 Pagination */}
          
//             <div className="flex gap-2 justify-end mt-4 items-center">
//               {/* Prev Button */}
//               <button
//                 disabled={currentPage === 1}
//                 onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
//                 className={`px-2 py-1 border rounded text-sm ${
//                   currentPage === 1
//                     ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//                     : "bg-white hover:bg-slate-100"
//                 }`}
//               >
//                 Prev
//               </button>

//               {/* Page Numbers */}
//               {(() => {
//                 const pages = [];
//                 const maxVisible = 5;
//                 let start = Math.max(1, currentPage - 2);
//                 let end = Math.min(totalPages, start + maxVisible - 1);

//                 if (end - start < maxVisible - 1) {
//                   start = Math.max(1, end - maxVisible + 1);
//                 }

//                 if (start > 1) {
//                   pages.push(1);
//                   if (start > 2) pages.push("...");
//                 }

//                 for (let i = start; i <= end; i++) pages.push(i);

//                 if (end < totalPages) {
//                   if (end < totalPages - 1) pages.push("...");
//                   pages.push(totalPages);
//                 }

//                 return pages.map((p, idx) =>
//                   p === "..." ? (
//                     <span key={idx} className="px-2 text-gray-500">
//                       ...
//                     </span>
//                   ) : (
//                     <button
//                       key={p}
//                       onClick={() => setCurrentPage(p)}
//                       className={`px-2 py-1 border rounded text-sm ${
//                         currentPage === p
//                           ? "bg-blue-600 text-white"
//                           : "bg-white hover:bg-slate-100"
//                       }`}
//                     >
//                       {p}
//                     </button>
//                   )
//                 );
//               })()}

//               {/* Next Button */}
//               <button
//                 disabled={currentPage === totalPages}
//                 onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
//                 className={`px-2 py-1 border rounded text-sm ${
//                   currentPage === totalPages
//                     ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//                     : "bg-white hover:bg-slate-100"
//                 }`}
//               >
//                 Next
//               </button>
//             </div>

//         </>
//       )}
//     </Card>
//   );
// }



// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Card from "../Components/Card";
// import SimpleTable from "../Components/SimpleTable";
// import Badge from "../Components/Badge";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import BlueButton from "../Components/BlueButton";

// export default function PullbackKeysReport({ brand, CompanyID, receiverId }) {
//   const [pullbackReports, setPullbackReports] = useState([]);
//   const [reportPage, setReportPage] = useState(1);
//   const [receivedTotalPages, setReceivedTotalPages] = useState(1);
//   const [loadingReceived, setLoadingReceived] = useState(false);
//   const [errorReceived, setErrorReceived] = useState(null);
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");

//   const rowsPerPage = 10;

//   useEffect(() => {
//     const fetchReceivedReports = async () => {
//       setLoadingReceived(true);
//       setErrorReceived(null);
//       try {
//         // 🧠 Dynamic params
//         const params = {
//           mode: 2, // mode=2 means pullback
//           page: reportPage,
//           per_page: rowsPerPage,
//         };

//         // If receiverId is provided → show that user’s pullback reports
//         if (receiverId) {
//           params.receiver_id = receiverId;
//         } else {
//           // Otherwise show all company-level reports
//           params.company_id = CompanyID;
//         }

//         if (fromDate) params.from_date = fromDate;
//         if (toDate) params.to_date = toDate;

//         const res = await axios.get("https://spillas.com/api/v1/keys/report", {
//           params,
//         });

//         setPullbackReports(res.data.data || []);
//         setReceivedTotalPages(res.data.last_page || 1);
//       } catch (err) {
//         console.error("Failed to fetch pullback reports:", err);
//         setErrorReceived("Failed to load pullback report");
//       } finally {
//         setLoadingReceived(false);
//       }
//     };

//     fetchReceivedReports();
//   }, [CompanyID, receiverId, reportPage, fromDate, toDate]);

//   return (
//     <Card brand={brand} title="Pullback Keys Report">
//       {/* Filter Bar */}
//       <div className="flex flex-wrap justify-end items-center mb-3 gap-3">
//         <label className="text-sm text-gray-600">From:</label>
//         <DatePicker
//           selected={fromDate ? new Date(fromDate) : null}
//           onChange={(date) =>
//             setFromDate(date ? date.toISOString().split("T")[0] : "")
//           }
//           dateFormat="yyyy-MM-dd"
//           className="border rounded-lg px-3 py-1 text-sm shadow-sm"
//           placeholderText="Start date"
//         />

//         <label className="text-sm text-gray-600">To:</label>
//         <DatePicker
//           selected={toDate ? new Date(toDate) : null}
//           onChange={(date) =>
//             setToDate(date ? date.toISOString().split("T")[0] : "")
//           }
//           dateFormat="yyyy-MM-dd"
//           className="border rounded-lg px-3 py-1 text-sm shadow-sm"
//           placeholderText="End date"
//         />

//         <BlueButton
//           brand={brand}
//           onClick={() => {
//             setFromDate("");
//             setToDate("");
//           }}
//           className="border px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
//         >
//           Reset
//         </BlueButton>
//       </div>

//       {/* Table Section */}
//       {loadingReceived ? (
//         <p>Loading...</p>
//       ) : errorReceived ? (
//         <p className="text-red-500">{errorReceived}</p>
//       ) : pullbackReports.length === 0 ? (
//         <p>No data found</p>
//       ) : (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No.",
//               "Date",
//               "Pullback From",
//               "Pullback To",
//               "Amount",
//               "Count",
//               "Status",
//             ]}
//             rows={pullbackReports.map((rep, i) => [
//               (reportPage - 1) * rowsPerPage + i + 1,
//               new Date(rep.created_at).toLocaleDateString(),
//               `${rep.sender_name} (${rep.sender_code || "N/A"})`,
//               `${rep.receiver_name} (${rep.receiver_code || "N/A"})`,
//               rep.amount,
//               rep.qty,
//               Badge("Received", "green"),
//             ])}
//           />

//           <div className="flex justify-center gap-2 mt-3">
//             <button
//               disabled={reportPage === 1}
//               onClick={() => setReportPage(reportPage - 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Prev
//             </button>
//             <span>
//               {reportPage}/{receivedTotalPages}
//             </span>
//             <button
//               disabled={reportPage === receivedTotalPages}
//               onClick={() => setReportPage(reportPage + 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         </>
//       )}
//     </Card>
//   );
// }


// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Card from "../Components/Card";
// import SimpleTable from "../Components/SimpleTable";
// import Badge from "../Components/Badge";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import BlueButton from "../Components/BlueButton";

// export default function PullbackKeysReport({ brand, CompanyID }) {
//   const [pullbackReports, setPullbackReports] = useState([]);
//   const [reportPage, setReportPage] = useState(1);
//   const [receivedTotalPages, setReceivedTotalPages] = useState(1);
//   const [loadingReceived, setLoadingReceived] = useState(false);
//   const [errorReceived, setErrorReceived] = useState(null);
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");

//   const rowsPerPage = 10;

//   useEffect(() => {
//     const fetchReceivedReports = async () => {
//       setLoadingReceived(true);
//       setErrorReceived(null);
//       try {
//         const params = {
//           mode: 2,
//           company_id: CompanyID,
//           page: reportPage,
//           per_page: rowsPerPage,
//         };

//         if (fromDate) params.from_date = fromDate;
//         if (toDate) params.to_date = toDate;

//         const res = await axios.get("https://spillas.com/api/v1/keys/report", {
//           params,
//         });

//         setPullbackReports(res.data.data || []);
//         setReceivedTotalPages(res.data.last_page || 1);
//       } catch (err) {
//         console.error("Failed to fetch pullback reports:", err);
//         setErrorReceived("Failed to load received report");
//       } finally {
//         setLoadingReceived(false);
//       }
//     };

//     fetchReceivedReports();
//   }, [CompanyID, reportPage, rowsPerPage, fromDate, toDate]);

//   return (
//     <Card brand={brand} title="Pullback Keys Report">
//       {/* Filter Bar */}
//       <div className="flex flex-wrap justify-end items-center mb-3 gap-3">
//         <label className="text-sm text-gray-600">From:</label>
//         <DatePicker
//           selected={fromDate ? new Date(fromDate) : null}
//           onChange={(date) =>
//             setFromDate(date ? date.toISOString().split("T")[0] : "")
//           }
//           dateFormat="yyyy-MM-dd"
//           className="border rounded-lg px-3 py-1 text-sm shadow-sm"
//           placeholderText="Start date"
//         />

//         <label className="text-sm text-gray-600">To:</label>
//         <DatePicker
//           selected={toDate ? new Date(toDate) : null}
//           onChange={(date) =>
//             setToDate(date ? date.toISOString().split("T")[0] : "")
//           }
//           dateFormat="yyyy-MM-dd"
//           className="border rounded-lg px-3 py-1 text-sm shadow-sm"
//           placeholderText="End date"
//         />

//         <BlueButton
//           brand={brand}
//           onClick={() => {
//             setFromDate("");
//             setToDate("");
//           }}
//           className="border px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
//         >
//           Reset
//         </BlueButton>
//       </div>

//       {/* Table Section */}
//       {loadingReceived ? (
//         <p>Loading...</p>
//       ) : errorReceived ? (
//         <p className="text-red-500">{errorReceived}</p>
//       ) : pullbackReports.length === 0 ? (
//         <p>No data found</p>
//       ) : (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No.",
//               "Date",
//               "Pullback from",
//               "Pullback to",
//               "Amount",
//               "Count",
//               "Status",
//             ]}
//             rows={pullbackReports.map((rep, i) => [
//               (reportPage - 1) * rowsPerPage + i + 1,
//               new Date(rep.created_at).toLocaleDateString(),
//               `${rep.sender_name} (${rep.sender_code || "N/A"})`,
//               `${rep.receiver_name} (${rep.receiver_code || "N/A"})`,
//               rep.amount,
//               rep.qty,
//               Badge("Received", "green"),
//             ])}
//           />

//           <div className="flex justify-center gap-2 mt-3">
//             <button
//               disabled={reportPage === 1}
//               onClick={() => setReportPage(reportPage - 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Prev
//             </button>
//             <span>
//               {reportPage}/{receivedTotalPages}
//             </span>
//             <button
//               disabled={reportPage === receivedTotalPages}
//               onClick={() => setReportPage(reportPage + 1)}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         </>
//       )}
//     </Card>
//   );
// }
